import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAuth, connectAuthEmulator, onAuthStateChanged } from 'firebase/auth';
import { getAnalytics, isSupported as analyticsIsSupported } from 'firebase/analytics';
import { FUNCTIONS_REGION } from './services/shared/cloudFunctions';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: "AIzaSyDedsQ2AyjRcw6ZRcAixdP3Ac26cIEf7Tc",
  authDomain: "m2b-tools.firebaseapp.com",
  projectId: "m2b-tools",
  storageBucket: "m2b-tools.firebasestorage.app",
  messagingSenderId: "1034251039593",
  appId: "1:1034251039593:web:f74336b0b607aca32303f8",
  measurementId: "G-JCHJ4D52SN"
};

let app;
let db;
let storage;
let functions;
let auth;
let analytics = null;
let authReady = Promise.resolve();

try {
  app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app, FUNCTIONS_REGION);
  auth = getAuth(app);

  // Analytics is browser-only (not available in SSR, test environments, or when
  // third-party cookies/scripts are blocked).
  analyticsIsSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });

  // Connect emulators if in development and configured
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    // Optionally connect auth emulator if you use it
    if (process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST) {
      // expected like "localhost:9099"
      const [host, portStr] = process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST.split(':');
      const port = parseInt(portStr, 10) || 9099;
      connectAuthEmulator(auth, `http://${host}:${port}`);
    }
    console.log('Connected to Firebase emulators');
  }

  // Resolve as soon as auth state is known — no anonymous sign-in needed because
  // Firestore security rules allow reading published events without authentication.
  authReady = new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
  });

  console.log('Firebase initialized successfully');
} catch (error) {
  console.error('Error initializing Firebase:', error);
  
  // Create a dummy Firestore object that logs errors instead of crashing
  db = {
    _isErrorObject: true,
    collection: () => {
      console.error('Firestore not initialized properly');
      return {
        doc: () => ({
          set: () => Promise.reject(new Error('Firebase not initialized')),
          get: () => Promise.reject(new Error('Firebase not initialized')),
          delete: () => Promise.reject(new Error('Firebase not initialized'))
        }),
        where: () => ({ get: () => Promise.reject(new Error('Firebase not initialized')) }),
        get: () => Promise.reject(new Error('Firebase not initialized'))
      };
    },
    doc: () => ({
      set: () => Promise.reject(new Error('Firebase not initialized')),
      get: () => Promise.reject(new Error('Firebase not initialized')),
      delete: () => Promise.reject(new Error('Firebase not initialized'))
    })
  };
  
  // Create a dummy Storage object
  storage = {
    _isErrorObject: true,
    ref: () => ({
      put: () => Promise.reject(new Error('Firebase Storage not initialized')),
      getDownloadURL: () => Promise.reject(new Error('Firebase Storage not initialized')),
      delete: () => Promise.reject(new Error('Firebase Storage not initialized'))
    })
  };

  // Create a dummy Functions object so callable lookups fail gracefully
  functions = { _isErrorObject: true };

  // create a no-op auth and resolved authReady so code doesn't hang
  auth = { _isErrorObject: true };
  authReady = Promise.resolve();
}

// Helper function to check if Cloud Functions are properly initialized
export const isFunctionsAvailable = () => {
  return !functions._isErrorObject;
};

// Helper function to check if Firestore is properly initialized
export const isFirestoreAvailable = () => {
  return !db._isErrorObject;
};

// Helper function to check if Storage is properly initialized
export const isStorageAvailable = () => {
  return !storage._isErrorObject;
};

export const getAnalyticsInstance = () => analytics;

// Export auth and authReady so other modules can await auth
export { app, db, storage, functions, auth, authReady, analytics };